// src/admin/MarqueeManagement.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Save, Sparkles, X, Eye, AlertCircle, Palette, Zap, Gauge
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const MarqueeManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);

  const defaultConfig = {
    text: 'Welcome to Organic Land | Fresh Organic Products Delivered | Free Shipping on Orders Rs.2000+ | 100% Organic Guaranteed',
    speed: 'normal',
    bgColor: '#16a34a',
    textColor: '#ffffff',
    iconColor: '#facc15',
    active: true,
  };

  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'marquee');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig({ ...defaultConfig, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await setDoc(doc(db, 'settings', 'marquee'), config, { merge: true });
      setMessage({ type: 'success', text: 'Marquee updated!' });

      // Notify website
      window.dispatchEvent(new Event('marqueeUpdated'));

      setShowSuccess(true);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save marquee.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    );
  }

  const messages = config.text.split('|').map(t => t.trim()).filter(Boolean);

  return (
    <div className="space-y-6 max-w-4xl">

      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Sparkles className="text-green-700" size={26} /> Marquee / Scrolling Text
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Website par chalne wali scrolling patti yahan se control karein.
        </p>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'error' && <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Live Preview */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Eye size={20} className="text-green-700" /> Live Preview
        </h2>
        <div
          className="rounded-lg overflow-hidden relative py-2.5 shadow-inner"
          style={{ backgroundColor: config.bgColor, color: config.textColor }}
        >
          <div className="flex items-center px-4 gap-4">
            {messages.map((m, i) => (
              <React.Fragment key={i}>
                <Sparkles size={14} style={{ color: config.iconColor }} fill={config.iconColor} className="shrink-0" />
                <span className="text-xs font-semibold tracking-wide whitespace-nowrap">{m}</span>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: config.iconColor }} />
              </React.Fragment>
            ))}
            {/* Repeat for visual clarity */}
            {messages.map((m, i) => (
              <React.Fragment key={`r-${i}`}>
                <Sparkles size={14} style={{ color: config.iconColor }} fill={config.iconColor} className="shrink-0" />
                <span className="text-xs font-semibold tracking-wide whitespace-nowrap">{m}</span>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: config.iconColor }} />
              </React.Fragment>
            ))}
          </div>
          <p className="absolute top-1 right-2 text-[10px] opacity-60">Preview</p>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Note: Ye sirf preview hai. Live animation website par chalegi.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* Text */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Text Messages</h2>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Messages (pipe <code className="bg-gray-100 px-1.5 py-0.5 rounded">|</code> se separate karein)
          </label>
          <textarea
            value={config.text}
            onChange={(e) => setConfig({ ...config, text: e.target.value })}
            rows={4}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none font-mono"
            placeholder="Welcome to Organic Land | Fresh Organic Products | Free Shipping"
          />
          <p className="text-xs text-gray-500 mt-2">
            💡 Har message ko <code className="bg-gray-100 px-1 rounded">|</code> se alag karein. Ye messages scroll hote rahenge.
          </p>

          {/* Message Chips */}
          {messages.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {messages.map((m, i) => (
                <span key={i} className="text-xs font-semibold px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                  {m.length > 30 ? m.slice(0, 30) + '...' : m}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Speed */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Gauge size={20} className="text-green-700" /> Animation Speed
          </h2>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'slow', label: 'Slow', icon: '🐢', duration: '40s' },
              { value: 'normal', label: 'Normal', icon: '⚡', duration: '25s' },
              { value: 'fast', label: 'Fast', icon: '🚀', duration: '15s' },
            ].map(s => (
              <button
                key={s.value}
                type="button"
                onClick={() => setConfig({ ...config, speed: s.value })}
                className={`p-4 rounded-xl border-2 text-center transition font-semibold ${
                  config.speed === s.value
                    ? 'border-green-600 bg-green-50 text-green-800'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-1">{s.icon}</div>
                <div className="text-sm">{s.label}</div>
                <div className="text-[10px] opacity-60 mt-0.5">{s.duration}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Colors */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Palette size={20} className="text-green-700" /> Colors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Background Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={config.bgColor}
                  onChange={(e) => setConfig({ ...config, bgColor: e.target.value })}
                  className="w-14 h-11 rounded-lg border-2 border-gray-300 cursor-pointer" />
                <input type="text" value={config.bgColor}
                  onChange={(e) => setConfig({ ...config, bgColor: e.target.value })}
                  className="flex-1 border border-gray-300 rounded-lg py-2.5 px-3 text-sm font-mono uppercase" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Text Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={config.textColor}
                  onChange={(e) => setConfig({ ...config, textColor: e.target.value })}
                  className="w-14 h-11 rounded-lg border-2 border-gray-300 cursor-pointer" />
                <input type="text" value={config.textColor}
                  onChange={(e) => setConfig({ ...config, textColor: e.target.value })}
                  className="flex-1 border border-gray-300 rounded-lg py-2.5 px-3 text-sm font-mono uppercase" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Icon Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={config.iconColor}
                  onChange={(e) => setConfig({ ...config, iconColor: e.target.value })}
                  className="w-14 h-11 rounded-lg border-2 border-gray-300 cursor-pointer" />
                <input type="text" value={config.iconColor}
                  onChange={(e) => setConfig({ ...config, iconColor: e.target.value })}
                  className="flex-1 border border-gray-300 rounded-lg py-2.5 px-3 text-sm font-mono uppercase" />
              </div>
            </div>
          </div>
        </div>

        {/* Active Toggle */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Zap size={20} className="text-green-700" /> Visibility
          </h2>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={config.active}
              onChange={(e) => setConfig({ ...config, active: e.target.checked })}
              className="w-5 h-5 text-green-700" />
            <span className="text-sm font-semibold text-gray-700">
              Show marquee on website
            </span>
          </label>
        </div>

        {/* Save */}
        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving}
            className={`bg-green-700 text-white py-3 px-8 rounded-lg font-semibold hover:bg-green-800 transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}>
            <Save size={18} /> {saving ? 'Saving...' : 'Save Marquee'}
          </button>
        </div>

      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="Marquee updated successfully!"
        buttonText="Great!"
      />

    </div>
  );
};

export default MarqueeManagement;